import { DownOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Input,
  Menu,
  message,
  Pagination,
  Select,
  Spin,
} from 'antd';
import Empty from 'components/Empty';
import SafeImage from 'components/SafeImage';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { useState } from 'react';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { getItems } from '../service';
import { formatNumber } from 'utils/numberFormat';

function ListSelect({
  negative,
  filter,
  value,
  onSelect,
  onDeselect,
}: {
  negative?: boolean;
  filter: Omit<ReviewAnalysis.OverviewFilter, 'spuIds'>;
  value: string[];
  onSelect: (items: ReviewAnalysis.Item[]) => void;
  onDeselect: (items: string[]) => void;
}) {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { config } = useStoreNameConfig();

  const [keywordType, setKeywordType] = useState<1 | 2 | 3>(1);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState({
    keyword,
    keywordType,
  });

  const { platform, storeInfo, stars, startTime, endTime, itemRange } = filter;

  const [sort, setSort] = useState<'TOTAL_COUNT_DESC' | 'BAD_COUNT_DESC'>('TOTAL_COUNT_DESC');

  const { loading, data, runAsync } = useRequest(
    ({ page, pageSize } = { page: 1, pageSize: 10 }) => {
      return getItems({
        keyword,
        keywordType,
        pageInfo: {
          page,
          pageSize,
        },
        overviewRequest: {
          platform,
          storeIds: storeInfo.list?.reduce<string[]>((res, { id }) => [...res, id], []) || [],
          stars,
          startTime,
          endTime,
          itemRange,
        },
        sort,
      });
    },
    {
      refreshDeps: [searchKeyword, platform, storeInfo, stars, startTime, endTime, sort],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  return (
    <Container>
      <header>
        <Input.Group compact>
          <Select
            dropdownMatchSelectWidth={false}
            value={keywordType}
            onChange={setKeywordType}
            options={[
              { value: 1, label: t('sider.商品名称') },
              { value: 2, label: t('sider.商品ID') },
              { value: 3, label: t('sider.货号') },
            ]}
          />
          <Input.Search
            allowClear
            placeholder={t('trade.请输入')}
            value={keyword}
            onChange={({ target: { value } }) => setKeyword(value)}
            onSearch={() => setSearchKeyword({ keyword, keywordType })}
          />
        </Input.Group>
      </header>
      {loading ? (
        <section>
          <Spin />
        </section>
      ) : data ? (
        <>
          <div>
            {data.itemData.length ? (
              <Checkbox
                checked={data.itemData.every(({ spuKey }) => value.includes(spuKey))}
                onChange={({ target: { checked } }) =>
                  checked
                    ? onSelect(data.itemData)
                    : onDeselect(data.itemData.map(({ spuKey }) => spuKey))
                }
              >
                {t('本页全选')}
              </Checkbox>
            ) : (
              <span />
            )}
            <Dropdown
              overlay={
                <Menu onClick={({ key }) => setSort(key as any)}>
                  <Menu.Item key="TOTAL_COUNT_DESC">{t('评价总数从多到少')}</Menu.Item>
                  <Menu.Item key="BAD_COUNT_DESC">{t('差评数量从多到少')}</Menu.Item>
                </Menu>
              }
            >
              <Button type="text">
                {sort === 'TOTAL_COUNT_DESC' ? t('评价总数从多到少') : t('差评数量从多到少')}
                <DownOutlined />
              </Button>
            </Dropdown>
          </div>
          <ul>
            {data.itemData.map((item) => {
              const {
                id,
                spuKey,
                img,
                name,
                platform,
                region,
                spuId,
                itemSku,
                storeName,
                storeAlias,
                reviewCount,
                negativeReviewsCount,
                negativeReviewsPercent,
              } = item;
              const checked = value.includes(spuKey);
              const storeAliasFirst = isStoreAliasFirst(config, platform, region);
              return (
                <li
                  key={id}
                  onClick={() => (checked ? onDeselect([item.spuKey]) : onSelect([item]))}
                >
                  <Checkbox checked={checked} />
                  <SafeImage width={64} height={64} src={img} />
                  <section>
                    <div>{name}</div>
                    <p>
                      {t('店铺')}：{platformLabelMap[toLowerCase(platform)]}-
                      {getRegionLabel(region)}-
                      {storeAliasFirst ? storeAlias || storeName : storeName}
                      <Divider type="vertical" />
                      ID：
                      {spuId}
                      <Divider type="vertical" />
                      {t('主商品货号')}：{itemSku}
                    </p>
                    <span>
                      {`${!negative ? `${t('评价总数')}：${reviewCount}   ` : ''}${t(
                        '差评数量',
                      )}：${negativeReviewsCount}   ${t('差评占比')}：${formatNumber(
                        Number(negativeReviewsPercent || 0) / 100,
                        '0.00%',
                      )}`}
                    </span>
                  </section>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <section>
          <Empty />
        </section>
      )}
      <footer>
        <Pagination
          showSizeChanger
          size="small"
          current={data?.page?.page || 1}
          pageSize={data?.page?.pageSize || 10}
          total={data?.page?.totalCount || 0}
          onChange={(page, pageSize) => runAsync({ page, pageSize })}
        />
      </footer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  > header {
    flex: 0 0 auto;
    padding: 8px 24px;
    border-bottom: solid 1px #f0f2f5;
    > span {
      display: flex;
      .ant-input-search {
        width: 260px;
      }
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  > div {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 8px 24px;
    > button {
      padding-right: 0;
    }
  }
  > ul {
    flex: 1 0 0;
    margin: 0;
    padding: 0 24px;
    overflow: auto;
    > li {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      padding: 10px;
      border: solid 1px #d3d7e0;
      cursor: pointer;
      > label,
      > div {
        flex: 0 0 auto;
        margin-right: 10px;
      }
      > section {
        flex: 1 0 0;
        overflow: hidden;
        > div {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        > p {
          margin: 4px 0;
          font-size: 12px;
          color: #84868c;
        }
        > span {
          font-size: 12px;
        }
      }
    }
  }
  > footer {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 48px;
    padding: 0 24px;
    box-shadow: 0 -2px 8px 0 #a7b2cc1a, inset 0 1px 0 0 #f0f2f5;
  }
`;

export default ListSelect;
